import { HugeiconsIcon } from '@hugeicons/react';
import {
  Mic01Icon,
  FileEditIcon,
  Calendar03Icon,
  Mail01Icon,
  ArrowRight01Icon,
  SparklesIcon,
  Clock01Icon,
} from '@hugeicons/core-free-icons';
import { useNavigate } from 'react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/common/Bits';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { cn } from '@/lib/utils';

const INTAKE_MODES = [
  {
    id: 'conversacion',
    title: 'Conversación con VIA',
    description:
      'Cuéntanos tu idea por texto o audio. VIA te guía con preguntas simples y extrae la información por ti.',
    icon: Mic01Icon,
    time: '5-10 min',
    badge: 'Recomendado',
    badgeClass: 'bg-primary/10 text-primary border-primary/20',
    to: '/demanda/nueva/conversacion',
    highlight: true,
  },
  {
    id: 'rapido',
    title: 'Formulario rápido',
    description:
      'Responde 6 preguntas abiertas sobre tu idea. Sin tecnicismos, sin evaluación. Solo cuéntanos qué necesitas.',
    icon: FileEditIcon,
    time: '5 min',
    badge: null,
    to: '/demanda/nueva/rapido',
    highlight: false,
  },
];

// Correo de contacto para agendar reunión con el equipo de IA
const MEETING_EMAIL = 'ia.innovacion@ucchristus.cl';
const MEETING_SUBJECT = 'Solicitud de reunión - Nueva iniciativa de IA';
const MEETING_BODY = `Hola equipo de IA Innovación:

Me gustaría conversar con ustedes sobre una posible iniciativa de IA. Les comparto un resumen para que lleguemos con contexto a la reunión:

- ¿Qué problema o necesidad me gustaría resolver?
  [Descríbelo en 1-2 líneas]

- ¿A quién beneficia? (área, equipo, pacientes, etc.)
  [Responde aquí]

- ¿Qué me gustaría lograr con esto?
  [Responde aquí]

- Mi disponibilidad para reunirnos (días/horarios):
  [Responde aquí]

Gracias,
[Tu nombre / área]`;

const MEETING_MAILTO = `mailto:${MEETING_EMAIL}?subject=${encodeURIComponent(
  MEETING_SUBJECT,
)}&body=${encodeURIComponent(MEETING_BODY)}`;

export default function InitiativeIntake() {
  const navigate = useNavigate();

  const handleSelect = (mode) => {
    navigate(mode.to);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <MotionSection>
        <PageHeader
          title="Registrar una iniciativa"
          subtitle="Todas las ideas son importantes. Elige cómo prefieres contarnos la tuya."
          testId="intake-header"
          backTo="/demanda"
          backLabel="Volver a la bandeja"
        />
      </MotionSection>

      {/* Mensaje de bienvenida */}
      <MotionSection delay={0.1}>
        <div className="mb-8 p-4 rounded-xl bg-primary/5 border border-primary/10">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <HugeiconsIcon icon={SparklesIcon} size={16} strokeWidth={1.5} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Cada idea es un punto de partida</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Queremos conocer tu idea para ayudarte a darle forma. El equipo de IA trabaja contigo para que tu
                iniciativa avance, sin importar en qué etapa esté.
              </p>
            </div>
          </div>
        </div>
      </MotionSection>

      {/* Tarjetas de modo */}
      <StaggerContainer className="grid gap-4 sm:grid-cols-2" stagger={0.08}>
        {INTAKE_MODES.map((mode) => (
          <StaggerItem key={mode.id}>
            <Card
              className={cn(
                'group cursor-pointer transition-all duration-200 hover:shadow-md hover:border-primary/30',
                mode.highlight && 'border-primary/20 shadow-sm ring-1 ring-primary/10',
              )}
              onClick={() => handleSelect(mode)}
              data-testid={`intake-mode-${mode.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
                      mode.highlight
                        ? 'bg-primary/10 text-primary group-hover:bg-primary/15'
                        : 'bg-muted/50 text-muted-foreground group-hover:bg-muted/80',
                    )}>
                    <HugeiconsIcon icon={mode.icon} size={20} strokeWidth={1.5} />
                  </div>
                  {mode.badge && (
                    <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full border', mode.badgeClass)}>
                      {mode.badge}
                    </span>
                  )}
                </div>

                <h3 className="text-sm font-semibold text-foreground mb-1">{mode.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{mode.description}</p>

                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <HugeiconsIcon icon={Clock01Icon} size={12} strokeWidth={1.5} />
                    {mode.time}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Comenzar
                    <HugeiconsIcon icon={ArrowRight01Icon} size={14} strokeWidth={1.5} className="ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </StaggerItem>
        ))}
      </StaggerContainer>

      {/* Agendar reunión — enlace a correo con plantilla */}
      <MotionSection delay={0.3}>
        <div className="mt-6 flex flex-col items-center gap-2 rounded-xl border border-border bg-muted/20 p-5 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
              <HugeiconsIcon icon={Calendar03Icon} size={18} strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">¿Prefieres conversar en vivo?</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Escríbenos contándonos tu idea y coordinamos una reunión con el equipo de IA. El correo ya trae una
                plantilla para que solo completes lo tuyo.
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm" className="flex-shrink-0 border-border">
            <a href={MEETING_MAILTO} data-testid="intake-schedule-meeting">
              <HugeiconsIcon icon={Mail01Icon} size={14} strokeWidth={1.5} className="mr-1.5" />
              Agendar reunión
            </a>
          </Button>
        </div>
      </MotionSection>

      {/* Footer informativo */}
      <MotionSection delay={0.4}>
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            Tu progreso se guarda automáticamente. Puedes continuar en otro momento sin perder nada.
          </p>
        </div>
      </MotionSection>
    </div>
  );
}
